import { Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { RegularModal } from 'components'
import { Card } from 'components/Card'
import { GoogleMap } from 'components/GoogleMap'
import { Table } from 'components/Table'
import { parseISO } from 'date-fns'
import React, { useEffect, useMemo } from 'react'
import styled, { useTheme } from 'styled-components'
import { useBreadcrumbs } from 'utils/hooks'
import {
  enforceHourMinuteString,
  generateActivityMapColors,
  VehicleColorDictionary,
} from '../activities'
import { MainInfo, Map, TripInfo } from '../activities/components/detail'
import { SpecificConditionNode } from '../activities/types.graphql'
import { WorkPeriodNode } from './types.graphql'

const StyledTable = styled(Table)`
  grid-area: sums;
  margin: 0.5rem 0;
`

const StyledMap = styled(Map)``

const Content = styled(Card)`
  display: grid;
  grid-template-columns: 2fr minmax(300px, 1fr);
  grid-template-rows: 120px 380px auto;
  grid-template-areas:
    'main-info map'
    'trip-info map'
    'sums sums'
    'schedule  schedule';
  padding: 0;

  ${props => props.theme.media.mobile} {
    grid-template-columns: 100%;
    grid-template-rows: 280px auto;
    grid-template-areas:
      'map'
      'main-info'
      'trip-info'
      'sums'
      'schedule';

    overflow: hidden;
  }
`

const StyledRegularModal = styled(RegularModal)`
  padding: 0;

  & > div {
    display: none;
  }
  & > div.content {
    display: block;
  }
  .content {
    padding: 0;
  }
`

const CancelWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding: 0.5rem 0.5rem 1rem 1rem;

  & > i {
    cursor: pointer;
  }
`

export interface SpecificCondition extends SpecificConditionNode {
  datetimeStart: string
  datetimeEnd: string | null
}

interface WorkPeriodDetailProps {
  workPeriod: WorkPeriodNode
  registrationNumbers?: string[]
  onSubmit: (workPeriod: WorkPeriodNode | null) => void
}

export const WorkPeriodDetail: React.CFC<WorkPeriodDetailProps> = ({
  workPeriod,
  registrationNumbers = [],

  onSubmit,
}) => {
  const translations = useTranslate({
    loading: 'common.loading',
    drivingData: 'common.driving-data',
    diet: 'common.diet',
    salaryDay: 'common.salary-day',
    showSums: 'common.show-sums',

    activity: 'common.activity',
    value: 'common.value',
    timeDriving: 'common.driving',
    timeRest: 'common.rest',
    timePause: 'common.pause',
    timePaidRest: 'activities.paid-pause',
    timeAvailability: 'common.availability',
    timeOtherWork: 'common.other-work',
    timeOvertime: 'common.overtime',
    timeWorkTotal: 'common.total-work',
  })
  const theme = useTheme()

  const { setOverride, setOverrides, overrides } = useBreadcrumbs()

  const vehicleLocations = workPeriod?.drivenCoordinates ?? []
  const userId = workPeriod?.user.id ?? ''

  useEffect(() => {
    if (!workPeriod && overrides[userId] !== translations.loading)
      setOverride(userId, translations.loading)
    else if (workPeriod && overrides[userId] === translations.loading) {
      const { user } = workPeriod
      setOverrides({
        [user.id]: user.fullName,
      })
    }
  }, [
    setOverride,
    setOverrides,
    translations.loading,
    userId,
    overrides,
    workPeriod,
  ])

  // We want the color to be consistent for each vehicle. Vehicle Vin is used as a [key: color]
  const vehicleColors = useMemo<VehicleColorDictionary>(() => {
    const vehicleObject: VehicleColorDictionary = {}
    const vehicleList =
      workPeriod.drivenCoordinates
        .map(drivenCoordinate => drivenCoordinate.vehicleVin ?? 'Unknown')
        .filter(
          (value, index, vehicles) => vehicles.indexOf(value) === index
        ) ?? []
    const vehicleColorList = generateActivityMapColors(vehicleList.length)

    vehicleList.forEach((vehicle, idx) => {
      vehicleObject[vehicle] = vehicleColorList[idx] ?? '#0088FF'
    })

    return vehicleObject
  }, [workPeriod.drivenCoordinates])

  const user = workPeriod?.user ?? null

  return (
    <StyledRegularModal
      overflowY="auto"
      cancelOnClickOutside
      onCancel={() => onSubmit(null)}
      width="calc(100vw - 2rem)"
    >
      <CancelWrapper>
        <Icon icon="times" onClick={() => onSubmit(null)} />
      </CancelWrapper>

      <Content>
        <MainInfo
          user={user}
          date={parseISO(workPeriod.datetimeStart)}
          registrationNumbers={registrationNumbers}
          loading={false}
        />

        <TripInfo
          startTime={workPeriod?.datetimeStart}
          endTime={workPeriod?.datetimeEnd}
          startLocation={undefined}
          endLocation={undefined}
          timeWorkTotal={enforceHourMinuteString(
            workPeriod?.activityTimes?.timeWorkTotal
          )}
          distance={undefined}
          fuelConsumption={undefined}
          driverGrade={undefined}
          drivingGrade={undefined}
        />

        {vehicleLocations.length === 0 ? (
          <GoogleMap
            mapContainerStyle={{
              borderTopRightRadius: theme.sizes.defaultBorderRadius,
            }}
            options={{
              streetViewControl: false,
              zoomControl: false,
              mapTypeControl: false,
            }}
          />
        ) : (
          <StyledMap
            pathColors={vehicleColors}
            vehicleLocations={vehicleLocations}
          />
        )}

        {/* Map activities on a one line graph with different colors for each activityType. Similar to Schedule.tsx. Inline, not on a map. */}
        <StyledTable noBorders noShadow>
          <thead>
            <tr>
              <th>{translations.activity}</th>
              <th>{translations.value}</th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td>{translations.timeWorkTotal}</td>
              <td>
                {enforceHourMinuteString(
                  workPeriod?.activityTimes?.timeWorkTotal
                )}
              </td>
            </tr>

            <tr>
              <td>{translations.timeDriving}</td>
              <td>
                {enforceHourMinuteString(
                  workPeriod?.activityTimes?.timeDriving
                )}
              </td>
            </tr>

            <tr>
              <td>{translations.timeOtherWork}</td>
              <td>
                {enforceHourMinuteString(
                  workPeriod?.activityTimes?.timeOtherWork
                )}
              </td>
            </tr>

            <tr>
              <td>{translations.timeAvailability}</td>
              <td>
                {enforceHourMinuteString(
                  workPeriod?.activityTimes?.timeAvailability
                )}
              </td>
            </tr>

            <tr>
              <td>{translations.timePaidRest}</td>
              <td>
                {enforceHourMinuteString(
                  workPeriod?.activityTimes?.timePaidRest
                )}
              </td>
            </tr>
          </tbody>
        </StyledTable>
      </Content>
    </StyledRegularModal>
  )
}
